import { ArrowUpRight, BadgeCheck, Check, Minus, TriangleAlert, X } from 'lucide-react'

import { formatVatRate } from '@/components/invoices/IncomingInvoiceUploadModal'
import { Modal } from '@/components/ui/Modal'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { incomingInvoiceUrl } from '@/lib/payout-api'

type CheckState = 'ok' | 'warn' | 'unknown'

/**
 * Accorderen is een beslissing, dus dit scherm verschijnt altijd en niet alleen
 * als er iets mis is. Zonder dat zou je bij een stille goedkeuring nooit weten of
 * er gecontroleerd is of dat er niets te controleren viel; die twee zien er van
 * buiten hetzelfde uit. Wat het systeem zelf kon nagaan staat er daarom expliciet
 * bij, inclusief wat het niet kon nagaan.
 *
 * Vragen doet het niet: wie de factuur aanlevert legt een afwijking zelf uit, en
 * hier lees je die uitleg naast de bedragen.
 */
export function ApproveIncomingInvoiceModal({
  lineId,
  canViewPdf,
  share,
  invoiceNumber,
  invoicedAmount,
  invoicedTotal,
  difference,
  reason,
  pending,
  onClose,
  onConfirm,
  onDecline,
}: {
  lineId: string
  canViewPdf: boolean
  share: string
  invoiceNumber?: string | null
  invoicedAmount?: string | null
  invoicedTotal?: string | null
  difference: number
  reason?: string | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
  /** Weglaten in schermen zonder afkeurstroom; de knop verschijnt dan niet. */
  onDecline?: () => void
}) {
  const net = invoicedAmount ? toNumber(invoicedAmount) : null
  const gross = invoicedTotal ? toNumber(invoicedTotal) : null
  // Afgeleid en niet vastgelegd: het tarief van de ander ligt niet vast, maar wat
  // zijn factuur impliceert hoor je wel te zien voordat je accordeert.
  const vatRate = net !== null && gross !== null && net !== 0 ? (gross / net - 1) * 100 : null
  const explained = Boolean(reason?.trim())

  const checks: Array<{ state: CheckState; text: string }> = [
    net === null
      ? { state: 'unknown', text: 'Geen bedrag vastgelegd, dus niet te vergelijken met het aandeel' }
      : difference === 0
        ? { state: 'ok', text: 'Het gefactureerde bedrag komt overeen met het aandeel' }
        : {
            state: 'warn',
            text: `${formatEuros(Math.abs(difference))} ${difference > 0 ? 'hoger' : 'lager'} dan het aandeel, ${explained ? 'toegelicht door de aanleveraar' : 'zonder toelichting'}`,
          },
    vatRate === null
      ? { state: 'unknown', text: 'Btw-tarief niet af te leiden' }
      : Math.abs(vatRate) < 0.05
        ? { state: 'ok', text: 'Btw 0%, verlegd of vrijgesteld' }
        : Math.abs(vatRate - 21) < 0.05
          ? { state: 'ok', text: 'Btw 21%, het gebruikelijke tarief' }
          : { state: 'warn', text: `Btw ${formatVatRate(vatRate)}, een afwijkend tarief` },
  ]

  const worst: CheckState = checks.some((c) => 'warn' === c.state)
    ? 'warn'
    : checks.some((c) => 'unknown' === c.state)
      ? 'unknown'
      : 'ok'

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Inkoopfactuur accorderen"
      description={
        'ok' === worst
          ? 'De controles kloppen. Open de PDF als je hem er zelf naast wilt leggen.'
          : 'unknown' === worst
            ? 'Niet alles is te controleren. Open de PDF en beoordeel hem zelf.'
            : 'Er is iets om te beoordelen. Open de PDF en lees de toelichting van de aanleveraar.'
      }
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          {onDecline ? (
            <button
              type="button"
              className="btn-ghost text-status-overdue hover:bg-status-overdue/10"
              onClick={onDecline}
              disabled={pending}
            >
              <X className="h-3.5 w-3.5" />
              Afkeuren
            </button>
          ) : null}
          <button type="button" className="btn-primary" onClick={onConfirm} disabled={pending}>
            <BadgeCheck className="h-3.5 w-3.5" />
            Accorderen
          </button>
        </>
      }
    >
      <dl className="divide-y divide-hairline rounded-card border border-hairline">
        <div className="flex items-baseline justify-between gap-4 px-3 py-2">
          <dt className="text-sm text-ink-faint">Inkoopfactuur</dt>
          <dd className="text-sm text-ink">
            {canViewPdf ? (
              <a
                href={incomingInvoiceUrl(lineId)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-sm text-accent-700 hover:underline"
              >
                {invoiceNumber ?? 'Bekijk de PDF'}
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              (invoiceNumber ?? '-')
            )}
          </dd>
        </div>
        <Row label="Aandeel in de verdeling" value={formatEuros(toNumber(share))} money />
        <Row label="Gefactureerd excl. btw" value={net === null ? '-' : formatEuros(net)} money />
        {net !== null && difference !== 0 ? (
          <Row
            label="Verschil"
            value={`${difference > 0 ? '+' : '-'}${formatEuros(Math.abs(difference))}`}
            money
            accent
          />
        ) : null}
        <Row label="Totaal incl. btw" value={gross === null ? '-' : formatEuros(gross)} money />
        <Row label="Btw-tarief" value={vatRate === null ? '-' : formatVatRate(vatRate)} />
      </dl>

      <p className="label-caps mt-4">Controles</p>
      <ul className="mt-1 space-y-1">
        {checks.map((check) => (
          <li key={check.text} className="flex items-start gap-2 text-sm">
            <CheckIcon state={check.state} />
            <span className={'warn' === check.state ? 'text-ink' : 'text-ink-soft'}>{check.text}</span>
          </li>
        ))}
      </ul>

      {net !== null && difference !== 0 ? (
        <>
          <p className="label-caps mt-4">Toelichting van de aanleveraar</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{reason?.trim() || 'Geen toelichting gegeven.'}</p>
        </>
      ) : null}
    </Modal>
  )
}

function CheckIcon({ state }: { state: CheckState }) {
  if ('ok' === state) return <Check className="mt-0.5 h-4 w-4 shrink-0 text-status-paid" />
  if ('warn' === state) return <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-approval" />

  return <Minus className="mt-0.5 h-4 w-4 shrink-0 text-ink-faint" />
}

function Row({ label, value, money, accent }: { label: string; value: string; money?: boolean; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-3 py-2">
      <dt className="text-sm text-ink-faint">{label}</dt>
      <dd className={`text-sm ${money ? 'money' : ''} ${accent ? 'font-semibold text-ink' : 'text-ink'}`}>{value}</dd>
    </div>
  )
}
